import { Grid, Paper } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import PlanHeader from "../../../shared/enrollment/planCode/PlanHeader";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import EditIcon from "@mui/icons-material/Edit";
import { useCallback, useContext } from "react";
import { ThemeContext } from "../../../../contexts";
import { AcknowledgementProps } from "../../../../types/components/enrollmentProps.types";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PanToolIcon from "@mui/icons-material/PanTool";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import ClearIcon from "@mui/icons-material/Clear";
import "./enrollmentAcknowledgement.css";
import { Status } from "../../../../types/enrollment.types";
import downloadFile from "../../../../utils/commonFunctions/downloadFile";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import downloadFileNew from "../../../../utils/commonFunctions/downloadFileNew";

type StatusIcon = {
	[key in Status]: JSX.Element;
};

type StatusMessage = {
	[key in Status]: string;
};

const statusIcon: StatusIcon = {
	ACTIVE: <DoneAllIcon className="thank-icon" />,
	SUBMITTED: <DoneOutlineIcon className="thank-icon" />,
	REJECTED: <ClearIcon className="thank-icon" />,
	WAIVED: <PanToolIcon className="thank-icon" />,
	"WAIVED - WAITING FOR APPROVAL": <WatchLaterIcon className="thank-icon" />,
	OPENED: <></>,
	CLOSED: <></>
};

const statusMessage: StatusMessage = {
	ACTIVE: "Your enrollment is successfully Activated",
	SUBMITTED: "Your enrollment has been successfully Submitted",
	REJECTED: "Your enrollment has been Rejected",
	WAIVED: "Your enrollment is successfully waived",
	"WAIVED - WAITING FOR APPROVAL": "Your enrollment for waived is pending for approval",
	OPENED: "",
	CLOSED: ""
};

const EnrollmentAcknowledgement = ({
	idCardAccessPointRequired,
	dependentChangePointRequired,
	carrierPhoneNo,
	carrierWebsite,
	planDocument,
	planName,
	addDependentLink,
	removeDependentLink,
	planCode,
	status,
	customStatusMeesage
}: AcknowledgementProps): JSX.Element => {
	const { theme } = useContext(ThemeContext);
	// console.log("status", status);
	const handleDownloadPlanDocument = useCallback(() => {
		if (!planDocument) {
			alert("No plan document is available");
			return;
		} else downloadFileNew(planDocument);
	}, [planDocument]);

	return (
		<div className="enrollment-acknowledgement">
			<PlanHeader planCode={planCode} planName={planName} />
			<div className="theme-plan-section-margin" />
			{!["OPENED", "CLOSED"].includes(status) ? (
				<>
					<Grid container direction="column" alignItems="center">
						{statusIcon[status as keyof StatusIcon]}
					</Grid>
					<div className="thank-note">
						{customStatusMeesage && status === "SUBMITTED"
							? customStatusMeesage
							: statusMessage[status as keyof StatusMessage]}
					</div>
				</>
			) : null}
			<div className="theme-plan-section-margin" />

			{/* ── Benefit Confirmation Content Box ── */}
			<div
				style={{
					border: `1.5px solid ${theme.primary_color}22`,
					borderLeft: `4px solid ${theme.primary_color}`,
					borderRadius: "10px",
					backgroundColor: `${theme.primary_color}08`,
					padding: "20px 24px",
					marginBottom: "16px"
				}}
			>
				{/* Success message */}
				<div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "14px" }}>
					<CheckCircleOutlineIcon style={{ color: theme.primary_color, marginTop: "2px", flexShrink: 0 }} />
					<div>
						<p style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1a1a2e" }}>
							Your benefit selection has been successfully processed.
						</p>
						<p style={{ margin: "4px 0 0", fontSize: "13.5px", color: "#444" }}>
							You can review your plan details card from your <strong>Benefit Section</strong> at any
							time.
						</p>
						<p style={{ margin: "4px 0 0", fontSize: "13.5px", color: "#444" }}>
							For updates or support, please visit your portal or contact your benefits administrator.
						</p>
					</div>
				</div>

				{/* Plan Document & ID Card Links */}
				<div style={{ borderTop: `1px dashed ${theme.primary_color}33`, margin: "14px 0" }} />

				{/* Divider */}
				<div style={{ borderTop: `1px dashed ${theme.primary_color}33`, margin: "14px 0" }} />

				{/* Contact heading */}
				<p
					style={{
						margin: "0 0 12px",
						fontWeight: 700,
						fontSize: "13px",
						color: theme.primary_color,
						textTransform: "uppercase",
						letterSpacing: "0.6px"
					}}
				>
					For Portal-related Queries
				</p>

				{/* Contacts grid */}
				<div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
					{/* Contact card – Becca */}
					<div
						style={{
							flex: "1 1 180px",
							background: "#fff",
							border: `1px solid ${theme.primary_color}22`,
							borderRadius: "8px",
							padding: "12px 14px"
						}}
					>
						<p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: "13.5px", color: "#1a1a2e" }}>
							Becca Boland
						</p>
						<p style={{ margin: "0 0 6px", fontSize: "12px", color: theme.primary_color, fontWeight: 600 }}>
							Human Resources
						</p>
						<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
							<PhoneIcon style={{ fontSize: "14px", color: "#888" }} />
							<span style={{ fontSize: "13px", color: "#444" }}>(936) 525-0145</span>
						</div>
					</div>

					{/* Contact card – Jennifer */}
					<div
						style={{
							flex: "1 1 180px",
							background: "#fff",
							border: `1px solid ${theme.primary_color}22`,
							borderRadius: "8px",
							padding: "12px 14px"
						}}
					>
						<p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: "13.5px", color: "#1a1a2e" }}>
							Jennifer Robertson
						</p>
						<p style={{ margin: "0 0 6px", fontSize: "12px", color: theme.primary_color, fontWeight: 600 }}>
							Benefits
						</p>
						<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
							<PhoneIcon style={{ fontSize: "14px", color: "#888" }} />
							<span style={{ fontSize: "13px", color: "#444" }}>(936) 634-8155 ext 4212</span>
						</div>
					</div>

					{/* Contact card – Call Center */}
					<div
						style={{
							flex: "1 1 180px",
							background: "#fff",
							border: `1px solid ${theme.primary_color}22`,
							borderRadius: "8px",
							padding: "12px 14px"
						}}
					>
						<p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: "13.5px", color: "#1a1a2e" }}>
							Benefits Call Center
						</p>
						<p style={{ margin: "0 0 6px", fontSize: "12px", color: theme.primary_color, fontWeight: 600 }}>
							General Support
						</p>
						<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
							<PhoneIcon style={{ fontSize: "14px", color: "#888" }} />
							<span style={{ fontSize: "13px", color: "#444" }}>833-356-0852</span>
						</div>
					</div>
				</div>

				{/* Divider */}
				<div style={{ borderTop: `1px dashed ${theme.primary_color}33`, margin: "14px 0" }} />

				{/* Carrier contact row */}
			</div>
		</div>
	);
};

export default EnrollmentAcknowledgement;
